import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PatientContactRole } from './prevention.service';

/** Modalité d'accès patient (couche Offer, lot "UI admin des offres prévention") -- wording dérivé côté template, jamais stocké. */
export type AccessMode = 'WITHOUT_PRESCRIPTION' | 'PRESCRIPTION_REQUIRED' | 'PROGRAM_ELIGIBILITY' | 'OTHER';

/** Modalité de financement patient (couche Offer) -- même rationale que AccessMode. */
export type FundingType = 'PATIENT_PAID' | 'REIMBURSED' | 'REIMBURSED_IF_ELIGIBLE' | 'MIXED' | 'UNKNOWN';

/** Fourchette de prix ACTIVE d'une Offer pour UNE devise (Lot 9.0-E.E.B) -- jamais agrégée entre devises différentes. */
export interface OfferPriceRange {
  currency: string;
  minPrice: number;
  maxPrice: number;
}

/** Ligne "Mes Offres" (Lot 9.0-E.C/E.E.B) -- contrat volontairement léger, jamais bilans[]/additionalItems[] complets. */
export interface OrganizationOfferSummary {
  offerId: string;
  name: string;
  description: string | null;
  referenceOfferCode: string | null;
  bilanCount: number;
  active: boolean;
  activeSiteCount: number;
  priceRanges: OfferPriceRange[];
  hasPhoto: boolean;
  photoUpdatedAt: string | null;
}

/** Un bilan dans la composition d'une Offer -- jamais needId/coverage médicale. */
export interface OfferBilan {
  code: string;
  name: string;
  position: number;
}

/** Contenu supplémentaire display-only -- jamais un PreventionBilan. */
export interface OfferAdditionalItem {
  label: string;
  position: number;
}

/** Détail complet d'une Offer -- réponse de POST/GET/PUT /offers[/{offerId}]. */
export interface OrganizationOfferDetail {
  offerId: string;
  name: string;
  description: string | null;
  referenceOfferCode: string | null;
  bilans: OfferBilan[];
  additionalItems: OfferAdditionalItem[];
  active: boolean;
  hasPhoto: boolean;
  photoUpdatedAt: string | null;
  // Optionnels (lot "UI admin des offres prévention") -- le backend les renvoie TOUJOURS (jamais
  // omis, éventuellement null) ; optionnel ici uniquement pour ne pas casser les fixtures de test
  // antérieures à ce lot qui n'ont pas besoin d'en connaître la valeur.
  patientDescription?: string | null;
  patientProcedure?: string | null;
  accessMode?: AccessMode | null;
  fundingType?: FundingType | null;
  /** Défaut groupe (Lot "Modalité de réalisation (défaut groupe)", 2026-09-12) du badge patient
   *  "Réalisable aujourd'hui" -- toujours renseigné par le backend (jamais optionnel comme
   *  accessMode/fundingType), chaque site peut le surcharger (voir {@link OfferSiteAvailability}). */
  defaultImmediateFeasibility: boolean;
  /** Support multi-catégorie (backend V317) -- toujours renvoyé par le backend (défaut false) ;
   *  optionnel ici uniquement pour les fixtures antérieures : une clé absente vaut `false`, jamais
   *  interprétée autrement (voir `OfferFormComponent#ngOnInit`). */
  allowMultiCategory?: boolean;
}

export interface CreateOfferRequest {
  name: string;
  description: string | null;
  referenceOfferCode: string | null;
  bilanCodes: string[];
  additionalItems: string[];
  /** Optionnels (lot "UI admin des offres prévention") -- {@code undefined} et {@code null} sont
   *  équivalents pour le backend (JSON.stringify omet une clé {@code undefined}, un record Java
   *  traite une clé absente comme {@code null}) ; ce composant les envoie toujours explicitement. */
  patientDescription?: string | null;
  patientProcedure?: string | null;
  accessMode?: AccessMode | null;
  fundingType?: FundingType | null;
  defaultImmediateFeasibility: boolean;
  /** Support multi-catégorie (backend V317) -- OBLIGATOIRE côté TypeScript pour qu'aucun appelant
   *  ne puisse l'omettre (le backend lirait une clé absente comme `false`). Défaut `false`, jamais
   *  hérité d'un ReferenceOffer. */
  allowMultiCategory: boolean;
}

/** Jamais referenceOfferCode -- provenance immutable, absente du corps PUT (section 24 du lot). */
export interface UpdateOfferRequest {
  name: string;
  description: string | null;
  bilanCodes: string[];
  additionalItems: string[];
  patientDescription?: string | null;
  patientProcedure?: string | null;
  accessMode?: AccessMode | null;
  fundingType?: FundingType | null;
  defaultImmediateFeasibility: boolean;
  /** PUT intégral : toujours envoyé explicitement (jamais omis), jamais concerné par le verrou de
   *  composition côté backend -- modifiable même sur une offre active sur un site. */
  allowMultiCategory: boolean;
}

/** Contrat GET uniquement (Lot 9.0-E.D) -- écriture (setAvailability) appartient à 9.0-E.E.F. */
export interface OfferSiteAvailability {
  establishmentId: string;
  active: boolean;
  price: number | null;
  currency: string | null;
  immediateFeasibilityDefault: boolean | null;
  conditions: string | null;
  patientContactRole: PatientContactRole | null;
  commercialPreference: boolean;
  /** Vrai si un AUTRE Offer actif sur ce site a une composition de bilans strictement identique -- seul cas où "Offre privilégiée sur ce site" a un sens. */
  hasDuplicateCoverage: boolean;
}

/**
 * Une ligne du batch multi-site (Lot 9.0-E.E.F) -- reprend exactement `SiteAvailabilityRequest`
 * côté backend (`waitora-prevention`). Toujours envoyée complète par ce frontend (jamais de champ
 * omis au sein d'une ligne) -- seule la sémantique "ligne absente du batch = inchangée" (section 26
 * du lot) est partielle, jamais un champ partiel à l'intérieur d'une ligne envoyée.
 */
export interface SetSiteAvailabilityRequest {
  establishmentId: string;
  active: boolean;
  price: number | null;
  currency: string;
  /** null = pas d'override sur ce site, retombe sur le défaut groupe (Lot "Modalité de
   *  réalisation (défaut groupe)", 2026-09-12) -- même idiome que patientContactRole ci-dessous. */
  immediateFeasibilityDefault: boolean | null;
  conditions: string | null;
  patientContactRole: PatientContactRole | null;
  commercialPreference: boolean;
}

/** Corps de `PUT /offers/{offerId}/availability` -- batch PARTIEL, tout établissement absent de `sites` reste inchangé. */
export interface SetOfferAvailabilityBatchRequest {
  sites: SetSiteAvailabilityRequest[];
}

/** CRUD "Mes Offres" (Lot 9.0-E.C/D) -- organisation-scopée, jamais needId/coveredNeeds/medicalTier exposés. */
@Injectable({
  providedIn: 'root'
})
export class PreventionOffersService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/api/v1/prevention/offers`;

  getOffers(): Observable<OrganizationOfferSummary[]> {
    return this.http.get<OrganizationOfferSummary[]>(this.baseUrl);
  }

  getOffer(offerId: string): Observable<OrganizationOfferDetail> {
    return this.http.get<OrganizationOfferDetail>(`${this.baseUrl}/${offerId}`);
  }

  createOffer(request: CreateOfferRequest): Observable<OrganizationOfferDetail> {
    return this.http.post<OrganizationOfferDetail>(this.baseUrl, request);
  }

  updateOffer(offerId: string, request: UpdateOfferRequest): Observable<OrganizationOfferDetail> {
    return this.http.put<OrganizationOfferDetail>(`${this.baseUrl}/${offerId}`, request);
  }

  archiveOffer(offerId: string): Observable<OrganizationOfferDetail> {
    return this.http.put<OrganizationOfferDetail>(`${this.baseUrl}/${offerId}/archive`, {});
  }

  /** Nécessaire à l'UX de verrouillage/archivage (section 19/20/27 du lot E.E.D). */
  getAvailability(offerId: string): Observable<OfferSiteAvailability[]> {
    return this.http.get<OfferSiteAvailability[]>(`${this.baseUrl}/${offerId}/availability`);
  }

  /**
   * Batch multi-site (Lot 9.0-E.E.F) -- réponse SPARSE identique au GET : uniquement les
   * disponibilités ACTUELLEMENT actives après mutation, jamais un site explicitement soumis mais
   * redevenu inactif. Le backend reste seul autoritaire -- ne jamais supposer que le payload envoyé
   * égale l'état final, toujours reconstruire l'état local à partir de cette réponse (section 31).
   */
  setAvailability(offerId: string, request: SetOfferAvailabilityBatchRequest): Observable<OfferSiteAvailability[]> {
    return this.http.put<OfferSiteAvailability[]>(`${this.baseUrl}/${offerId}/availability`, request);
  }

  /** POST upload/remplacement de la photo de l'offre -- même idiome que EstablishmentsService#uploadPhoto. */
  uploadPhoto(offerId: string, file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<void>(`${this.baseUrl}/${offerId}/photo`, formData);
  }

  deletePhoto(offerId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${offerId}/photo`);
  }

  /** URL publique de la photo (pas d'auth requise -- un <img src> n'envoie jamais l'en-tête Authorization), avec cache-busting sur photoUpdatedAt. */
  getPhotoUrl(offerId: string, photoUpdatedAt?: string | null): string {
    const url = `${environment.apiUrl}/public/v1/prevention/offers/${offerId}/photo`;
    return photoUpdatedAt ? `${url}?v=${encodeURIComponent(photoUpdatedAt)}` : url;
  }
}
